/**
 * # ConfirmationModal
 *
 * The `ConfirmationModal` is a specialized modal component built on top of Albert's atomic design system.
 * It provides a standardized interface for confirmation dialogs with icon badges, structured content, and action buttons.
 * This component is ideal for delete confirmations, warning dialogs, and success notifications that require user acknowledgment.
 *
 * ## Props
 *
 * - `body` (React.ReactNode): Modal body content - supports plain text, JSX, or React components
 * - `confirmText` (string, required): Text displayed on the confirm/primary action button
 * - `onConfirm` (function, required): Callback function executed when confirm button is clicked
 * - `onCancel` (function, required): Callback function executed when cancel button is clicked or modal is dismissed
 * - `badgeColor` (IconColors): Color theme for the badge icon (e.g., 'positive', 'negative', 'warning', 'tertiary')
 * - `badgeIcon` (IconProp): FontAwesome icon name for the badge (e.g., 'trash', 'check-circle', 'exclamation-triangle')
 * - `title` (string): Modal title text displayed prominently below the badge
 * - `cancelText` (string): Text displayed on the cancel/secondary action button
 * - `confirmBtnColor` (ButtonColor): Color theme for confirm button (e.g., 'primary', 'negative', 'positive')
 * - `cancelBtnColor` (ButtonColor): Color theme for cancel button (defaults to 'secondary')
 * - `confirmBtnVariant` (ButtonVariant): Style variant for confirm button (e.g., 'solid', 'outlined')
 * - `cancelBtnVariant` (ButtonVariant): Style variant for cancel button (defaults to 'outlined')
 * - `buttonWrapperClassname` (string): Additional CSS classes for the button container for custom styling
 *
 * ## Usage
 *
 * ```tsx
 * import {ConfirmationModal} from '@albert-io/atomic';
 *
 * const DeleteAssignmentModal = ({assignment, onClose}) => {
 *   const handleDelete = async () => {
 *     await deleteAssignment(assignment.id);
 *     addToast({color: 'positive', title: 'Assignment deleted successfully'});
 *     onClose();
 *   };
 *
 *   return (
 *     <ConfirmationModal
 *       badgeIcon="trash"
 *       badgeColor="negative"
 *       title="Delete Assignment"
 *       body="Are you sure you want to delete this assignment? This action cannot be undone."
 *       confirmText="Delete"
 *       cancelText="Cancel"
 *       confirmBtnColor="negative"
 *       onConfirm={handleDelete}
 *       onCancel={onClose}
 *     />
 *   );
 * };
 * ```
 *
 */
import React from 'react';
import classnames from 'classnames';
import {Button, Dialogue, Modal, Text, Icon} from '@albert-io/atomic';
import {ButtonColor, ButtonVariant} from '@albert-io/atomic/atoms/Button/Button.react';
import {IconColors, IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

export interface ConfirmationModalProps {
  badgeColor?: IconColors;
  badgeIcon?: IconProp;
  title?: string;
  body: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmBtnColor?: ButtonColor;
  cancelBtnColor?: ButtonColor;
  confirmBtnVariant?: ButtonVariant;
  cancelBtnVariant?: ButtonVariant;
  onCancel: () => void;
  onConfirm: () => void;
  buttonWrapperClassname?: string;
  className?: string;
  dismissable?: boolean;
  hideCloseBtn?: boolean;
}

export const ConfirmationModal = ({
  badgeColor,
  badgeIcon,
  title,
  body,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmBtnColor,
  confirmBtnVariant,
  cancelBtnColor = 'secondary',
  cancelBtnVariant = 'outlined',
  onCancel,
  onConfirm,
  buttonWrapperClassname,
  className,
  dismissable = true,
  hideCloseBtn = false
}: ConfirmationModalProps) => {
  return (
    <Modal
      ariaLabel={title ?? 'Confirmation Modal'}
      handleClose={onCancel}
      dismissable={dismissable}
    >
      {({CloseButtonWrapper, modalContentStyle}) => (
        <Dialogue
          inModal
          paddingLevel={2}
          className={classnames(modalContentStyle, className)}
          {...(hideCloseBtn ? {hideCloseBtn: true} : {handleClose: onCancel})}
        >
          <Dialogue.Body className='pt-x2 pr-x2 pl-x2'>
            <div className='flex flex-col items-center justify-center text-center'>
              {badgeIcon && <Icon className='fa-2x' icon={badgeIcon} color={badgeColor} />}
              {title && (
                <Text as='div' bold size='l' className='mt-x5'>
                  {title}
                </Text>
              )}
              <div className='mt-x2'>{body}</div>
            </div>
          </Dialogue.Body>
          <Dialogue.BtnGroup align='center' className={classnames('pb-x2', buttonWrapperClassname)}>
            <CloseButtonWrapper>
              <Button color={cancelBtnColor} variant={cancelBtnVariant} onClick={onCancel}>
                {cancelText}
              </Button>
            </CloseButtonWrapper>
            <Button color={confirmBtnColor} variant={confirmBtnVariant} onClick={onConfirm}>
              {confirmText}
            </Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
};

export default ConfirmationModal;
